import React, { useState, useEffect, useCallback } from 'react';

interface MagicLinkAuthProps {
  onSuccess: () => void;
  onError: (error: string) => void;
}

const MagicLinkAuth: React.FC<MagicLinkAuthProps> = ({ onSuccess, onError }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const handleMagicLinkVerification = useCallback(async () => {
    try {
      setLoading(true);

      // Get email from localStorage or URL parameter
      let savedEmail = window.localStorage.getItem('emailForSignIn');

      // If not in localStorage, check URL parameter
      if (!savedEmail) {
        const urlParams = new URLSearchParams(window.location.search);
        savedEmail = urlParams.get('email');
      }

      if (!savedEmail) {
        onError('Email not found. Please request a new sign-in link.');
        return;
      }

      console.log('[MagicLink] Verifying magic link for email:', savedEmail);

      // Use Firebase's built-in email link verification (client-side)
      const { signInWithEmailLink, isSignInWithEmailLink } = await import('firebase/auth');
      const { auth } = await import('../config/firebase');

      // Check if this URL is a sign-in link
      if (!isSignInWithEmailLink(auth, window.location.href)) {
        throw new Error('Invalid sign-in link');
      }

      // Sign in with email link directly through Firebase
      const result = await signInWithEmailLink(auth, savedEmail, window.location.href);

      console.log('[MagicLink] Firebase sign-in successful!');

      // Now we need to call the backend to create/update the user profile in Firestore
      // This ensures the user document exists with organization field
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const idToken = await result.user.getIdToken();

      await fetch(`${backendUrl}/auth/magic-link/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ email: savedEmail })
      });

      console.log('[MagicLink] User profile created/updated');

      // Clear email from localStorage
      window.localStorage.removeItem('emailForSignIn');

      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);

      onSuccess();
    } catch (error: any) {
      console.error('[MagicLink] Verification error:', error);

      // Provide user-friendly error messages
      let errorMessage = 'Failed to verify sign-in link';
      if (error.code === 'auth/invalid-action-code') {
        errorMessage = 'This sign-in link has expired or is invalid. Please request a new one.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.message) {
        errorMessage = error.message;
      }

      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [onSuccess, onError]);

  // Check if URL contains magic link parameters (after clicking email link)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const oobCode = urlParams.get('oobCode');
    const apiKey = urlParams.get('apiKey');

    if (mode === 'signIn' && oobCode && apiKey) {
      console.log('[MagicLink] Magic link detected in URL, verifying...');
      handleMagicLinkVerification();
    }
  }, [handleMagicLinkVerification]);

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedEmail = email.toLowerCase().trim();

    // Validate email
    if (!normalizedEmail) {
      onError('Please enter your email address');
      return;
    }

    if (!normalizedEmail.endsWith('@culturegenesis-partnerships.com')) {
      onError('Magic link authentication is only available for Culture Genesis Partnerships email addresses');
      return;
    }

    setLoading(true);

    try {
      // Use Firebase's built-in email sending (client-side)
      const { sendSignInLinkToEmail } = await import('firebase/auth');
      const { auth } = await import('../config/firebase');

      const frontendUrl = window.location.origin;
      const actionCodeSettings = {
        url: `${frontendUrl}/?email=${encodeURIComponent(normalizedEmail)}`,
        handleCodeInApp: true,
      };

      console.log('[MagicLink] Sending sign-in link via Firebase...');

      // Firebase automatically sends the email
      await sendSignInLinkToEmail(auth, normalizedEmail, actionCodeSettings);

      console.log('[MagicLink] Sign-in link sent successfully by Firebase');

      // Save email to localStorage for verification later
      window.localStorage.setItem('emailForSignIn', normalizedEmail);

      setLinkSent(true);
    } catch (error: any) {
      console.error('[MagicLink] Send error:', error);

      // Provide user-friendly error messages
      let errorMessage = 'Failed to send sign-in link';
      if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/missing-android-pkg-name') {
        errorMessage = 'App configuration error. Please contact support.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (linkSent) {
    return (
      <div style={styles.container}>
        <div style={styles.successMessage}>
          <h3 style={styles.successTitle}>✉️ Check your email!</h3>
          <p style={styles.successText}>
            We've sent a sign-in link to <strong>{email}</strong>
          </p>
          <p style={styles.successSubtext}>
            Click the link in your email to sign in. The link will expire in 1 hour.
          </p>

          <div style={styles.instructionsBox}>
            <p style={styles.instructionsTitle}>📝 Next steps:</p>
            <ol style={styles.instructionsList}>
              <li>Open your email inbox</li>
              <li>Look for an email from Culture Genius</li>
              <li>Click the "Sign In" link in the email</li>
              <li>You'll be automatically signed in</li>
            </ol>
          </div>

          <button
            onClick={() => {
              setLinkSent(false);
              setEmail('');
            }}
            style={styles.backButton}
          >
            Use different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSendMagicLink} style={styles.form}>
      <div style={styles.inputGroup}>
        <label htmlFor="email" style={styles.label}>
          Partner Email Address
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@culturegenesis-partnerships.com"
          disabled={loading}
          style={styles.input}
          autoComplete="email"
          autoFocus
        />
      </div>

      <button
        type="submit"
        disabled={loading || !email}
        style={{
          ...styles.button,
          ...(loading || !email ? styles.buttonDisabled : {})
        }}
      >
        {loading ? 'Sending...' : 'Send sign-in link'}
      </button>

      <p style={styles.hint}>
        You'll receive an email with a link to sign in.
        <br />
        No password required.
      </p>
    </form>
  );
};

const styles = {
  container: {
    width: '100%',
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-lg)',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-sm)',
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--fg-1)',
  },
  input: {
    width: '100%',
    padding: '0.75rem var(--space-md)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-1)',
    fontSize: '0.9375rem',
    backgroundColor: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    transition: 'border-color 0.15s ease',
    boxSizing: 'border-box' as const,
  },
  button: {
    backgroundColor: 'var(--accent-2)',
    color: 'white',
    border: 'none',
    padding: '0.75rem var(--space-lg)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9375rem',
    fontWeight: 500,
    cursor: 'pointer',
    width: '100%',
    transition: 'all 0.15s ease',
    boxShadow: 'var(--shadow-sm)',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  hint: {
    fontSize: '0.8125rem',
    color: 'var(--fg-3)',
    textAlign: 'center' as const,
    lineHeight: 1.5,
    marginTop: '-0.5rem',
  },
  successMessage: {
    textAlign: 'center' as const,
    padding: 'var(--space-lg)',
  },
  successTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: 'var(--fg-1)',
    marginBottom: 'var(--space-md)',
  },
  successText: {
    fontSize: '0.9375rem',
    color: 'var(--fg-2)',
    marginBottom: 'var(--space-sm)',
  },
  successSubtext: {
    fontSize: '0.875rem',
    color: 'var(--fg-3)',
    marginBottom: 'var(--space-lg)',
    lineHeight: 1.5,
  },
  instructionsBox: {
    backgroundColor: 'rgba(79, 70, 229, 0.05)',
    border: '1px solid rgba(79, 70, 229, 0.2)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-md)',
    marginBottom: 'var(--space-lg)',
    textAlign: 'left' as const,
  },
  instructionsTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--accent)',
    marginBottom: 'var(--space-sm)',
  },
  instructionsList: {
    fontSize: '0.875rem',
    color: 'var(--fg-2)',
    lineHeight: 1.6,
    paddingLeft: '1.25rem',
    margin: 0,
  },
  backButton: {
    backgroundColor: 'transparent',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-1)',
    padding: '0.5rem var(--space-md)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
};

export default MagicLinkAuth;
